import React from 'react';

const StyleGuide: React.FC = () => {
  return (
    <div className="container py-8">
      <h1 className="text-5xl font-bold mb-8 text-gradient">Assertion Style Guide</h1>
      
      {/* Typography Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Typography</h2>
        <div className="space-y-4">
          <h1 className="text-5xl">Heading 1 - Inter 48px</h1>
          <h2 className="text-4xl">Heading 2 - Inter 36px</h2>
          <h3 className="text-3xl">Heading 3 - Inter 30px</h3>
          <h4 className="text-2xl">Heading 4 - Inter 24px</h4>
          <h5 className="text-xl">Heading 5 - Inter 20px</h5>
          <h6 className="text-lg">Heading 6 - Inter 18px</h6>
          <p className="text-base">Body text - Inter 16px regular weight</p>
          <p className="text-sm text-muted">Small text - Inter 14px muted color</p>
          <p className="text-xs text-subtle">Extra small text - Inter 12px subtle color</p>
          <p className="font-light">Light weight (300)</p>
          <p className="font-normal">Normal weight (400)</p>
          <p className="font-medium">Medium weight (500)</p>
          <p className="font-semibold">Semibold weight (600)</p>
          <p className="font-bold">Bold weight (700)</p>
          <code className="font-mono">Monospace text - JetBrains Mono</code>
        </div>
      </section>

      {/* Colors Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Colors</h2>
        
        <h3 className="text-xl font-medium mb-4">Brand Colors</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--assertion-orange)' }}></div>
            <p className="text-sm font-medium">Assertion Orange</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--assertion-black)' }}></div>
            <p className="text-sm font-medium">Assertion Black</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--gradient-orange)' }}></div>
            <p className="text-sm font-medium">Orange Gradient</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--gradient-dark)' }}></div>
            <p className="text-sm font-medium">Dark Gradient</p>
          </div>
        </div>

        <h3 className="text-xl font-medium mb-4">Gray Scale</h3>
        <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-8">
          {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(shade => (
            <div key={shade} className="text-center">
              <div 
                className="h-16 rounded mb-1" 
                style={{ background: `var(--gray-${shade})` }}
              ></div>
              <p className="text-xs">{shade}</p>
            </div>
          ))}
        </div>

        <h3 className="text-xl font-medium mb-4">Semantic Colors</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--color-success)' }}></div>
            <p className="text-sm font-medium">Success</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--color-warning)' }}></div>
            <p className="text-sm font-medium">Warning</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--color-error)' }}></div>
            <p className="text-sm font-medium">Error</p>
          </div>
          <div className="text-center">
            <div className="h-24 rounded-lg mb-2" style={{ background: 'var(--color-info)' }}></div>
            <p className="text-sm font-medium">Info</p>
          </div>
        </div>
      </section>

      {/* Buttons Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Buttons</h2>
        
        <h3 className="text-xl font-medium mb-4">Button Variants</h3>
        <div className="flex flex-wrap gap-4 mb-8">
          <button className="btn btn-primary btn-md">Primary Button</button>
          <button className="btn btn-secondary btn-md">Secondary Button</button>
          <button className="btn btn-outline btn-md">Outline Button</button>
          <button className="btn btn-ghost btn-md">Ghost Button</button>
          <button className="btn btn-danger btn-md">Danger Button</button>
        </div>

        <h3 className="text-xl font-medium mb-4">Button Sizes</h3>
        <div className="flex items-end flex-wrap gap-4 mb-8">
          <button className="btn btn-primary btn-sm">Small</button>
          <button className="btn btn-primary btn-md">Medium</button>
          <button className="btn btn-primary btn-lg">Large</button>
        </div>

        <h3 className="text-xl font-medium mb-4">Button States</h3>
        <div className="flex flex-wrap gap-4 mb-8">
          <button className="btn btn-primary btn-md">Normal</button>
          <button className="btn btn-primary btn-md hover-lift">Hover Effect</button>
          <button className="btn btn-primary btn-md btn-loading">Loading</button>
          <button className="btn btn-primary btn-md" disabled>Disabled</button>
        </div>

        <h3 className="text-xl font-medium mb-4">Icon Buttons</h3>
        <div className="flex items-center gap-4">
          <button className="btn btn-primary btn-icon btn-sm">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
          </button>
          <button className="btn btn-secondary btn-icon btn-md">
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
          </button>
          <button className="btn btn-ghost btn-icon btn-lg">
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
          </button>
        </div>
      </section>

      {/* Forms Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Forms</h2>
        
        <div className="max-w-2xl">
          <div className="form-group">
            <label className="form-label">Input Field</label>
            <input type="text" className="form-input" placeholder="Enter text..." />
            <p className="form-helper">This is helper text</p>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Required Field</label>
            <input type="email" className="form-input" placeholder="email@example.com" />
          </div>

          <div className="form-group">
            <label className="form-label">Error State</label>
            <input type="text" className="form-input form-input-error" placeholder="Invalid input" />
            <p className="form-error-message">This field has an error</p>
          </div>

          <div className="form-group">
            <label className="form-label">Success State</label>
            <input type="text" className="form-input form-input-success" defaultValue="Valid input" />
          </div>

          <div className="form-group">
            <label className="form-label">Textarea</label>
            <textarea className="form-textarea" rows={4} placeholder="Enter multiple lines..."></textarea>
          </div>

          <div className="form-group">
            <label className="form-label">Select</label>
            <select className="form-select">
              <option>Option 1</option>
              <option>Option 2</option>
              <option>Option 3</option>
            </select>
          </div>

          <div className="form-group">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="form-label mb-0">Checkbox</span>
            </label>
          </div>

          <div className="form-group">
            <label className="flex items-center gap-2">
              <input type="radio" name="radio" className="form-radio" />
              <span className="form-label mb-0">Radio Option 1</span>
            </label>
            <label className="flex items-center gap-2 mt-2">
              <input type="radio" name="radio" className="form-radio" />
              <span className="form-label mb-0">Radio Option 2</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Toggle Switch</label>
            <label className="form-switch">
              <input type="checkbox" />
              <span className="form-switch-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">File Input</label>
            <div className="form-file">
              <input type="file" id="file-input" />
              <label htmlFor="file-input" className="form-file-label">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                  <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"/>
                </svg>
                Choose File
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Input Sizes</label>
            <div className="space-y-2">
              <input type="text" className="form-input form-input-sm" placeholder="Small input" />
              <input type="text" className="form-input" placeholder="Medium input" />
              <input type="text" className="form-input form-input-lg" placeholder="Large input" />
            </div>
          </div>
        </div>
      </section>

      {/* Spacing Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Spacing</h2>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16].map(space => (
            <div key={space} className="flex items-center gap-4">
              <span className="text-sm font-mono w-16">space-{space}</span>
              <div className={`h-4 bg-assertion-orange rounded`} style={{ width: `var(--space-${space})` }}></div>
              <span className="text-xs text-muted">{space * 4}px</span>
            </div>
          ))}
        </div>
      </section>

      {/* Animations Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Animations</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="h-24 bg-gradient-orange rounded-lg animate-fadeIn mb-2"></div>
            <p className="text-sm">Fade In</p>
          </div>
          <div className="text-center">
            <div className="h-24 bg-gradient-orange rounded-lg animate-scaleIn mb-2"></div>
            <p className="text-sm">Scale In</p>
          </div>
          <div className="text-center">
            <div className="h-24 bg-gradient-orange rounded-lg animate-pulse mb-2"></div>
            <p className="text-sm">Pulse</p>
          </div>
          <div className="text-center">
            <div className="h-24 bg-gradient-orange rounded-lg flex items-center justify-center mb-2">
              <div className="w-8 h-8 border-4 border-white border-r-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-sm">Spin</p>
          </div>
        </div>

        <h3 className="text-xl font-medium mb-4 mt-8">Hover Effects</h3>
        <div className="flex gap-4">
          <div className="p-4 bg-gray-100 rounded-lg hover-lift cursor-pointer">
            <p>Hover Lift</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-lg hover-grow cursor-pointer">
            <p>Hover Grow</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-lg hover-shrink cursor-pointer">
            <p>Hover Shrink</p>
          </div>
        </div>

        <h3 className="text-xl font-medium mb-4 mt-8">Loading States</h3>
        <div className="space-y-4">
          <div className="skeleton skeleton-text w-3/4"></div>
          <div className="skeleton skeleton-title w-1/2"></div>
          <div className="skeleton skeleton-button"></div>
          <div className="skeleton skeleton-card"></div>
        </div>
      </section>

      {/* Grid System Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Grid System</h2>
        
        <h3 className="text-xl font-medium mb-4">Responsive Grid</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm">Col 1</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm">Col 2</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm">Col 3</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm">Col 4</p>
          </div>
        </div>
      </section>

      {/* Shadows Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-semibold mb-6">Shadows</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="p-6 bg-white rounded-lg" style={{ boxShadow: 'var(--shadow-xs)' }}>
            <p className="text-sm font-medium">XS Shadow</p>
          </div>
          <div className="p-6 bg-white rounded-lg" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <p className="text-sm font-medium">SM Shadow</p>
          </div>
          <div className="p-6 bg-white rounded-lg" style={{ boxShadow: 'var(--shadow-md)' }}>
            <p className="text-sm font-medium">MD Shadow</p>
          </div>
          <div className="p-6 bg-white rounded-lg" style={{ boxShadow: 'var(--shadow-lg)' }}>
            <p className="text-sm font-medium">LG Shadow</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StyleGuide;